import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic, MicOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { usePressTail } from '@/components/voice/usePressTail'
import { useSpeechRecognition } from '@/components/voice/useSpeechRecognition'

/**
 * Inline push-to-talk mic button for Eric chat surfaces (Ask-Eric
 * widget, Messaging continue dialog). Lives where the old toggle-mic
 * sat — same icon-button footprint, but the gesture is press-and-hold
 * instead of tap-to-open-drawer.
 *
 * Why no separate voice drawer in chat:
 *   - The drawer + Use-raw button forced two clicks (open drawer →
 *     record → confirm) when the user just wants "speak, see text".
 *   - Android Chrome dictation was glitching mid-utterance on
 *     continuous-mode Web Speech sessions (the toggle UX held the
 *     recogniser open). Shorter PTT presses sidestep that.
 *   - For Eric chat there's no "Summarize with Eric" step — Eric IS
 *     who you're talking to — so the drawer added no value.
 *
 * Appointment voice notes keep the full `VoiceRecorder` because the
 * Summarize step is meaningful there (the transcript becomes a shop
 * note, not a chat prompt).
 *
 * Gesture model:
 *   - pointerdown → start recogniser
 *   - pointerup / pointercancel → stop recogniser, delivered by the
 *     shared `usePressTail` window tail (see its header for why
 *     element-scoped handlers aren't enough)
 *   - on stop, when `useSpeechRecognition` settles to 'idle' with a
 *     non-empty transcript, fire `onTranscript(text)` and reset.
 */

export type PushToTalkMicProps = {
  onTranscript: (text: string) => void
  disabled?: boolean
  className?: string
  // Optional override for the i18n key shown in the aria-label /
  // tooltip when idle. Defaults to `voice_recorder.hold_to_talk`.
  idleLabelKey?: string
}

export function PushToTalkMic({
  onTranscript,
  disabled,
  className,
  idleLabelKey = 'hold_to_talk',
}: PushToTalkMicProps) {
  const { t } = useTranslation('voice_recorder')
  const speech = useSpeechRecognition()
  const pressedRef = useRef(false)
  // Track the last transcript we already emitted so the useEffect
  // below doesn't fire twice if a Strict-Mode double-render or a
  // status churn re-evaluates the condition with stale state.
  const emittedRef = useRef('')

  // When the recogniser settles back to idle after a press, push the
  // accumulated transcript up to the parent and reset for the next
  // utterance. Status transitions: listening → idle on .stop(); the
  // hook's onresult callback may still flush the last final segment
  // synchronously before onend, so by the time status hits idle the
  // transcript ref is settled.
  useEffect(() => {
    if (speech.status !== 'idle') return
    const text = speech.transcript.trim()
    if (text.length === 0) return
    if (text === emittedRef.current) return
    emittedRef.current = text
    onTranscript(text)
    speech.reset()
    emittedRef.current = ''
  }, [speech, onTranscript])

  // Re-sync after a stop the press didn't deliver (the 60s cap on a ghost
  // lost release, an error, a service self-end): the recogniser is down but
  // pressedRef would stay true and silently kill the NEXT press's start().
  useEffect(() => {
    if (speech.status !== 'listening') pressedRef.current = false
  }, [speech.status])

  // Same start-side re-entrancy guard as VoiceRecorder: a second finger's
  // pointerdown re-keys the tail silently; an unguarded start() would orphan
  // the live recogniser. The tail delivers release/cancel at most once.
  const startListening = () => {
    if (pressedRef.current) return
    pressedRef.current = true
    speech.start()
  }
  const stopListening = () => {
    if (!pressedRef.current) return
    pressedRef.current = false
    speech.stop()
  }

  // Window-owned press tail (see usePressTail.ts for the delivery contract).
  const tail = usePressTail({
    onRelease: stopListening,
    onCancel: stopListening,
  })

  if (speech.status === 'unsupported') {
    // Web Speech API not available (Firefox today, embedded webviews).
    // Hide the button entirely rather than render a dead-looking
    // control — the user can type into the textarea instead.
    return null
  }

  const listening = speech.status === 'listening'
  const labelIdle = t(idleLabelKey, t('hold_to_talk'))
  const labelLive = t('listening')

  return (
    <Button
      type="button"
      variant={listening ? 'destructive' : 'outline'}
      size="icon"
      disabled={disabled}
      onPointerDown={(e) => {
        e.preventDefault()
        tail.begin(e)
        startListening()
      }}
      // Block the right-click + long-press context menus that would
      // steal the press gesture on mobile Safari + Android; the callout
      // property is iOS Safari's variant of the trap.
      onContextMenu={(e) => e.preventDefault()}
      aria-label={listening ? labelLive : labelIdle}
      aria-pressed={listening}
      title={listening ? labelLive : labelIdle}
      className={`select-none touch-none [-webkit-touch-callout:none] ${className ?? ''}`}
    >
      {listening ? <MicOff className="size-4" /> : <Mic className="size-4" />}
    </Button>
  )
}
